import type { HierarchyNode } from "./hierarchy.ts";
import type { TranslationCode } from "./translations.ts";
import type { Passage } from "./reference.ts";
import type { SectionNode } from "./sections.ts";

/** The HTTP contract shared by the Worker and the web app. */

export type Difficulty = 0 | 1 | 2 | 3;
export const DIFFICULTY_LABELS = ["Easy", "Medium", "Hard", "Very Hard"] as const;

export interface ApiErrorBody {
  error: { code: string; message: string };
}

export interface ConfigResponse {
  name: string;
  launchDate: string;
  hierarchy: readonly HierarchyNode[];
  translation: { name: string; note: string };
  /** Versions this deployment can serve text in (always includes WEB). The `tr` query parameter takes one of these. */
  translations: TranslationCode[];
  /** Changes whenever any book's section labels change. Send it as `?v=` so cached trees are never stale. */
  sectionsVersion: number;
  /** Web Push public key (base64url). Null when daily reminders aren't set up on this server, which hides the feature. */
  pushPublicKey: string | null;
}

export interface ChallengeSummary {
  number: number;
  date: string;
}

export interface ChallengeListResponse {
  /** Highest playable game number for the caller's date; 0 if none yet. */
  latest: number;
  challenges: ChallengeSummary[];
}

export interface ChallengeResponse {
  number: number;
  date: string;
  /** Base difficulty. Also the number of hints the player gets. */
  difficulty: Difficulty;
  hints: number;
  /** Passage text; names replaced with "[redacted]" when `redactable`. */
  text: string;
  /** True if `text` has hidden names the player may choose to reveal (raising ease, lowering shown difficulty). */
  redactable: boolean;
  note: string | null;
}

/** A verse someone sent. Same payload as a daily game, plus who it's from. `number` is always 0. */
export interface SentPuzzleResponse extends ChallengeResponse {
  code: string;
  from: string | null;
}

export interface SendRequest {
  bookId: number;
  startChapter: number;
  startVerse: number;
  endChapter: number;
  endVerse: number;
  /** One of SEND_MESSAGES' keys, or omitted for no message. */
  messageKey?: string | null;
  from?: string | null;
}

export interface SendResponse {
  code: string;
  /** Secret that lets the sender retract this verse. Returned once; only its hash is stored. */
  manageToken: string;
}

export interface SentStatsResponse {
  solves: number;
  averageStars: number | null;
}

export interface BibleChapterResponse {
  bookId: number;
  chapter: number;
  verses: Array<{ verse: number; text: string }>;
}

export interface UnredactedResponse {
  text: string;
}

/** A guess at one level of a round. `prefix` is the already-correct path; `index` is the option picked. */
export type GuessRequest =
  | { round: 1; prefix: number[]; index: number }
  | { round: 2; bookPath: number[]; prefix: number[]; index: number }
  | { round: 3; bookPath: number[]; sectionPath: number[]; index: number };

export interface GuessResponse {
  correct: boolean;
  /** Only for a wrong guess: which way the right answer lies relative to the guess. */
  direction?: "above" | "below";
  /** True when this correct guess finished the round. */
  done?: boolean;
}

export type HintRequest =
  | { round: 1; prefix: number[]; guessed: number[] }
  | { round: 2; bookPath: number[]; prefix: number[]; guessed: number[] }
  | { round: 3; bookPath: number[]; sectionPath: number[]; guessed: number[] };

export interface HintResponse {
  /** The correct option plus about half of the remaining wrong ones. */
  highlighted: number[];
}

export interface BookSectionsResponse {
  bookId: number;
  name: string;
  tree: SectionNode[];
}

/** A neighbouring verse. Deliberately unnumbered: a gap in the numbering would give the answer away. */
export interface Round3Verse {
  text: string;
  redacted: string;
}

export interface Round3Response {
  /** Neighbouring verses of the section with the target removed. The answer is one of `verses.length + 1` slots. */
  verses: Round3Verse[];
  slots: number;
}

export interface RevealResponse {
  reference: string;
  /** For a sent verse: who sent it. */
  from?: string | null;
  /** The same passage in structured form (lets the UI offer "send this verse to a friend"). */
  passage: Passage;
  text: string;
  trivia: string | null;
  note: string | null;
}

export interface HealthResponse {
  status: "ok" | "low" | "empty" | "uninitialized";
  launchDate: string | null;
  todayNumber: number | null;
  latestNumber: number;
  bufferDays: number | null;
  lastCronAt: string | null;
  /** Scheduled games from today on that still have no trivia. */
  pendingTrivia?: number;
  /** Which LLM the daily job uses to write them, or null if none is configured. */
  llm?: string | null;
}
